import React from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import Modal from "@cloudscape-design/components/modal";
import { Box, Button, Container, Flashbar, FormField, Header, Input, Pagination, Select, SpaceBetween, Table, Tabs, TextFilter, Toggle } from "@cloudscape-design/components";

import { gamesMetaTagsStore } from "../stores/gamesTagsModal";
import GamesMetaTagDetail from "./GamesMetaTagDetail";
import GamesMetaTagsStat from "./GamesMetaTagsStat";
import { userStore } from "../stores/access";

class GamesTagsModal extends React.Component {
	constructor(props) {
		super(props);
		this.debounceSearchTimeout = null;
		this.translateTypes = [
			{ label: "Local", value: "local" },
			{ label: "International", value: "international" },
		];
		this.state = {
			createMetaTag: { name: "", translate: "", translateType: "", active: true },
			createdMetaTag: null,
			metaTagDetails: null,
			errors: {},
			createLoading: false,
		};
	}

	clearFieldError(fieldName) {
		if (this.state.errors[fieldName]) {
			this.setState((prev) => {
				const newErrors = { ...prev.errors };
				delete newErrors[fieldName];
				return { errors: newErrors };
			});
		}
	}

	updateField(fieldName, value) {
		this.setState((prev) => ({
			createMetaTag: {
				...prev.createMetaTag,
				[fieldName]: value,
			},
		}));
		this.clearFieldError(fieldName);
	}

	setFilter(options) {
		this.props.setMetaTags((prev) => ({
			...prev,
			filters: {
				...prev.filters,
				...options,
			},
		}));
	}

	validateTag() {
		const { createMetaTag } = this.state;
		const errors = {};
		if (!createMetaTag.name.trim()) errors.name = "Name is required";
		if (!createMetaTag.translate.trim()) errors.translate = "Translate is required";
		if (!createMetaTag.translateType) errors.translateType = "Translate type is required";
		this.setState({ errors });
		return Object.keys(errors).length === 0;
	}

	handleCreateTag() {
		if (!this.validateTag()) return;
		this.setState({ createLoading: true });

		this.props.Interface.createGameMetaTag(this.state.createMetaTag)
			.then((data) => {
				this.setState({ createMetaTag: { name: "", translate: "" }, createdMetaTag: data.id ? data : null });
			})
			.finally(() => {
				this.setState({ createLoading: false });
			});
	}

	handleClearFilter() {
		this.setFilter({
			sorting: {
				column: "id",
				direction: "asc",
			},
			search: "",
			page: 1,
			limit: 10,
		});
		setTimeout(() => {
			this.props.Interface.loadGameMetaTags();
		}, 50);
	}

	renderCreate() {
		return (
			<Box>
				<Container header={<Header variant="h3">Create Meta Tag</Header>}>
					<SpaceBetween direction="vertical" size="l">
						<FormField stretch>
							<SpaceBetween direction="horizontal" size="s">
								<b>Active:</b>
								<Toggle label="Active" onChange={({ detail }) => this.updateField("active", detail.checked)} checked={this.state.createMetaTag.active}></Toggle>
							</SpaceBetween>
						</FormField>
						<FormField label="Name" errorText={this.state.errors.name} stretch>
							<Input placeholder="Name" value={this.state.createMetaTag.name} onChange={({ detail }) => this.updateField("name", detail.value)} />
						</FormField>
						<FormField label="Translate Type" errorText={this.state.errors.translateType} stretch>
							<Select placeholder="Select translate type" onChange={({ detail }) => this.updateField("translateType", detail.selectedOption.value)} selectedOption={this.translateTypes.find((option) => option.value === this.state.createMetaTag.translateType)} value={this.state.createMetaTag.translateType} options={this.translateTypes} />
						</FormField>
						<FormField label="Translate" errorText={this.state.errors.translate} stretch>
							<Input placeholder="Translate" value={this.state.createMetaTag.translate} onChange={({ detail }) => this.updateField("translate", detail.value)} />
						</FormField>
					</SpaceBetween>
					<Box variant="div" size="s" margin={{ top: "l" }}>
						<SpaceBetween direction="horizontal" size="s">
							<Button loading={this.state.createLoading} onClick={() => this.handleCreateTag()} variant="primary">
								Save
							</Button>
							<Button
								variant="link"
								onClick={() => {
									this.setState({ createMetaTag: { name: "", translate: "", metaTag: [], tags: [] } });
								}}>
								Clear
							</Button>
						</SpaceBetween>
					</Box>
				</Container>
				{this.state.createdMetaTag && (
					<Box margin={{ top: "l" }}>
						<Flashbar
							items={[
								{
									type: "success",
									content: "The notification has been successfully created, you can go to it",
									action: (
										<Button
											onClick={() => {
												this.setState({ metaTagDetails: this.state.createdMetaTag, createdMetaTag: null });
											}}>
											Go to meta tags
										</Button>
									),
									dismissible: true,
									dismissLabel: "Dismiss message",
									onDismiss: () => {
										this.setState({ createdMetaTag: null });
									},
									id: "message_1",
								},
							]}
						/>
					</Box>
				)}
			</Box>
		);
	}

	renderTable() {
		return (
			<Table
				columnDefinitions={[
					{
						id: "id",
						header: "ID",
						sortingField: "id",
						minWidth: 90,
						cell: (item) => <Button onClick={() => this.setState({ metaTagDetails: item })}>{item.id}</Button>,
					},
					{
						id: "name",
						header: "Name",
						sortingField: "name",
						cell: (item) => <span style={{ maxWidth: "100px", display: "block", whiteSpace: "normal" }}>{item.name}</span>,
					},
					{
						id: "translate",
						header: "Translate",
						sortingField: "translate",
						cell: (item) => <span style={{ maxWidth: "100px", display: "block", whiteSpace: "normal" }}>{item.translate}</span>,
					},

					{
						id: "active",
						header: "Active",
						sortingField: "active",
						cell: (item) => <>{item.active ? "Yes" : "No"}</>,
					},

					{
						id: "createdAt",
						header: "Created At",
						sortingField: "createdAt",
						cell: (item) => <>{new Date(item.createdAt).toLocaleDateString() + " " + new Date(item.createdAt).toLocaleTimeString()}</>,
					},

					{
						id: "action",
						header: "Action",
						minWidth: 130,
						maxWidth: 130,
						cell: (item) => (
							<Button variant="link" onClick={() => this.setState({ metaTagDetails: item })}>
								Edit
							</Button>
						),
					},
				]}
				enableKeyboardNavigation
				items={this.props.metaTags.data}
				loading={!this.props.metaTags.loaded}
				sortingColumn={{ sortingField: this.props.metaTags.filters.sorting.column }}
				sortingDescending={this.props.metaTags.filters.sorting.order === "desc"}
				onSortingChange={({ detail }) => {
					this.setFilter({
						sorting: { column: detail.sortingColumn.sortingField, order: detail.isDescending ? "desc" : "asc" },
					});
					setTimeout(() => {
						this.props.Interface.loadGameMetaTags();
					}, 50);
				}}
				trackBy="name"
				empty={
					<Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
						<SpaceBetween>
							<b>No meta tags</b>
							<Button variant="link" onClick={() => this.handleClearFilter()}>
								Clear filters
							</Button>
						</SpaceBetween>
					</Box>
				}
				filter={
					<TextFilter
						filteringPlaceholder="Find tag"
						filteringText={this.props.metaTags.filters.search}
						onChange={({ detail }) => {
							this.setFilter({
								search: detail.filteringText,
							});
							if (this.debounceSearchTimeout) clearTimeout(this.debounceSearchTimeout);
							this.debounceSearchTimeout = setTimeout(() => {
								this.setFilter({
									page: 1,
								});
								setTimeout(() => {
									this.props.Interface.loadGameMetaTags();
								}, 50);
							}, 400);
						}}
					/>
				}
				header={
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
						<Header variant="h3">Table Meta Tags</Header>
					</div>
				}
				pagination={
					<Pagination
						currentPageIndex={this.props.metaTags.filters.page}
						onChange={({ detail }) => {
							this.setFilter({
								page: detail.currentPageIndex,
							});
							setTimeout(() => {
								this.props.Interface.loadGameMetaTags();
							}, 50);
						}}
						pagesCount={this.props.metaTags.totalPages}
						disabled={this.props.metaTags.totalPages === 1}
					/>
				}
			/>
		);
	}

	componentDidMount() {
		this.props.Interface.loadGameMetaTags();
	}

	render() {
		return (
			<>
				<Modal
					visible={this.props.metaTags.show}
					onDismiss={() => {
						this.props.setMetaTags({ ...this.props.metaTags, show: false });
					}}
					size="large"
					header={this.state.metaTagDetails ? `Meta Tag ${this.state.metaTagDetails.name}` : "Games Meta Tags"}
					children={
						this.state.metaTagDetails ? (
							<GamesMetaTagDetail Interface={this.props.Interface} metaTagDetails={this.state.metaTagDetails} setMetaTagDetails={(metaTag) => this.setState({ metaTagDetails: metaTag })} />
						) : (
							<Tabs
								onChange={() => {
									this.setState({ createdMetaTag: null });
								}}
								tabs={[
									{
										label: "Tags List",
										id: "first",
										content: this.renderTable(),
									},
									{
										label: "Create Meta Tag",
										id: "second",
										content: this.renderCreate(),
									},
									{
										label: "Statistics",
										id: "third",
										content: <GamesMetaTagsStat Interface={this.props.Interface} handleShowMetaTagDetail={(metaTag) => this.setState({ metaTagDetails: metaTag })} />,
									},
								].filter((tab) => (tab.id === "third" ? this.props.user.showColumn.includes("metaTagStatistics") || this.props.user.level >= 3 : true))}
							/>
						)
					}
					footer={
						<>
							<Box
								float="right"
								children={
									<Box
										variant="span"
										margin={{ right: "s" }}
										children={
											<Button
												variant="normal"
												onClick={() => {
													this.props.setMetaTags({ ...this.props.metaTags, show: false });
												}}
												children="Close"
											/>
										}
									/>
								}
							/>
						</>
					}
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const user = useRecoilValue(userStore);
		const metaTags = useRecoilValue(gamesMetaTagsStore);
		const setMetaTags = useSetRecoilState(gamesMetaTagsStore);

		return <Component {...props} user={user} metaTags={metaTags} setMetaTags={setMetaTags} />;
	};
}

export default withRecoil(GamesTagsModal);
